import { Fragment, useEffect, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { signIn } from 'next-auth/react';
import LoadingCircle from '#components/feedback/loadingCircle/LoadingCircle';
import { useOvermindActions, useOvermindState } from '#store/overmind';
import { tagUserLogin } from '#utils/tagging/tag/tagUserAction';
import ProfileSelection from '#modules/profileSelection/ProfileSelection';
import TeaserDetailsContainer from '#components/teaser-details/TeaserDetailsContainer';
import isAppWebview from '#utils/isAppWebview';
import getConfigVars from '#utils/get-config-vars';
import ScrollToTopButton from '#components/scroll/ScrollToTopButton';

// const CheckSession = dynamic(() => import('#components/iframe/CheckSession'), {
//   ssr: false,
// });

const PageFooter = dynamic(() => import('#components/pagefooter/PageFooter'), {
  ssr: false,
});

const BasePageLayout = ({ children, hasCanonical = true }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const {
    app: { profileSelectionVisible, isLoadingIndicatorVisible },
    user: { isLoggedIn, profile, loading: userIsLoading },
    session: { initialized, token },
  } = useOvermindState();
  const { setIsLoadingIndicatorVisible, toggleProfileMenu } =
    useOvermindActions().app;
  const { initSession } = useOvermindActions().session;

  useEffect(() => {
    initSession();
  }, []);

  useEffect(() => {
    const handleStart = (url) => {
      const isSameUrl = url === router.pathname;
      setIsLoadingIndicatorVisible(!isSameUrl);
      toggleProfileMenu(false);
    };
    const handleComplete = () => {
      setIsLoadingIndicatorVisible(false);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);
  }, [router]);

  useEffect(() => {
    if (isLoggedIn && !profile) {
      tagUserLogin({ user_properties: { user_logged_in: true } });
    }
  }, [isLoggedIn, profile]);

  useEffect(() => {
    if (initialized && !token && router?.query?.forceLogin === 'true') {
      const callbackUrl = `/${router.locale}${router.asPath}`;
      signIn('cidaas', { callbackUrl });
    }
  }, [initialized]);

  const showProfileSelectView =
    (isLoggedIn && !profile && !userIsLoading) || profileSelectionVisible;

  const { BASE_URL } = getConfigVars({
    BASE_URL: 'https://www.veejoy.de',
  });
  const [canonicalUrl, setCanonicalUrl] = useState('');

  useEffect(() => {
    /** the default locale 'de' is not displayed in the url, so we need to hide it */
    const canonicalLocale = router.locale === 'de' ? '' : '/' + router.locale;

    /** asPath on the homepage is '/', we need to not display it, to avoid urls like /en/, since we are using /en */
    const canonicalPath =
      router.asPath !== '/' ? router.asPath.split('?')[0] : '';
    setCanonicalUrl(BASE_URL + canonicalLocale + canonicalPath);
  }, [router.pathname]);

  return (
    <Fragment>
      <Head>
        {hasCanonical && canonicalUrl && (
          <link rel="canonical" href={canonicalUrl} />
        )}
        <meta
          name="description"
          content={t('defaultMetaDescription')}
          key="metaDescription"
        />
      </Head>

      {/*{token && <CheckSession />}*/}

      {showProfileSelectView && <ProfileSelection />}

      {!showProfileSelectView && (
        <>
          <LoadingCircle isVisible={isLoadingIndicatorVisible} />
          {children}
          <TeaserDetailsContainer />
          {!isAppWebview() && <PageFooter />}
        </>
      )}

      <ScrollToTopButton />
    </Fragment>
  );
};

export default BasePageLayout;
